import Dialog from '@/global/guides/Dialog'
import { cn } from '@/lib/utils'
import axios from 'axios'
import { DownloadIcon, ExternalLink, UnlinkIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import IconButton from '../Button/IconButton'

interface IProps {
    file?: string | File | null
    open: boolean
    onClose: () => void
}

/**
 * Visualizador do arquivo do `FormFile`: imagem ou PDF em diálogo. Aceita tanto
 * o `File` recém-escolhido quanto a URL vinda da API.
 *
 * Uso interno do `FormFile` — não é um diálogo de propósito geral.
 */
export function DialogFile({ file: image, open, onClose }: IProps) {
    // Função utilitária para obter a extensão do arquivo
    function getFileExtension(filename?: string | null) {
        if (!filename) return ''
        return filename.split('.').pop()?.toLowerCase() || ''
    }

    const isFile = image instanceof File
    const fileName = isFile ? image.name : image
    const extension = getFileExtension(fileName)

    /** `File` bruto não existe no servidor — vira object URL local, revogado ao trocar de arquivo. */
    const [objectUrl, setObjectUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!isFile) return setObjectUrl(null)

        const created = URL.createObjectURL(image)
        setObjectUrl(created)
        return () => URL.revokeObjectURL(created)
    }, [image, isFile])

    const url = isFile
        ? objectUrl
        : `${import.meta.env.VITE_BASE_URL}/v1/uploads/${image}`

    const baixar = (href: string) => {
        const link = document.createElement('a')
        link.href = href
        link.setAttribute('download', fileName!)
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    const handleClick = async () => {
        if (!url) return
        if (isFile) return baixar(url)

        const response = await axios.get(url, { responseType: 'blob' })
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        baixar(blobUrl)
        window.URL.revokeObjectURL(blobUrl)
    }

    // Função para renderizar o conteúdo conforme o tipo do arquivo
    function renderFileContent() {
        if (!image || !url) return null

        const isImagem = isFile
            ? image.type.startsWith('image/')
            : ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
                  extension,
              )

        if (isImagem) {
            return <img src={url} alt="dialog-file" />
        }

        const isVideo = isFile
            ? image.type.startsWith('video/')
            : ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(extension)

        if (isVideo) {
            return (
                <video controls>
                    <source
                        src={url}
                        type={isFile ? image.type : `video/${extension}`}
                    />
                    Seu navegador não suporta o elemento de vídeo.
                </video>
            )
        }

        return (
            <div
                className={cn(
                    'bg-card rounded-lg h-[calc(100vh/2)] p-4',
                    'flex items-center justify-center gap-4 select-none',
                )}
            >
                <UnlinkIcon className="size-8" />
                <span className="text-xl">
                    Pré-visualização não suportada para este tipo de arquivo.
                </span>
            </div>
        )
    }

    return (
        <Dialog.Root
            open={open}
            onClose={onClose}
            className="bg-transparent border-none"
            width="6xl"
        >
            <div className="relative pt-8 my-auto">
                <div
                    className={cn(
                        'absolute top-0 right-0 z-2',
                        '*:bg-background *:text-muted-foreground',
                        '*:hover:bg-accent *:hover:text-foreground transition-colors',
                        '*:ring *:text-base *:shadow-sm',
                        'flex items-center gap-2 p-1',
                        'firefox-blur-md bg-accent',
                        '*size-8 p-2',
                        '*:rounded-full',
                        'rounded-full',
                        '*:gap-2',
                    )}
                >
                    <IconButton
                        onClick={() => {
                            if (!url) return
                            window.open(url, '_blank', 'noopener,noreferrer')
                        }}
                        title="Abrir em nova aba"
                    >
                        <ExternalLink />
                    </IconButton>
                    <IconButton onClick={handleClick} title="Baixar arquivo">
                        <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={onClose} title="Fechar">
                        <XIcon className="size-6" />
                    </IconButton>
                </div>
                <div
                    className={cn(
                        'flex items-center justify-center',
                        'bg-transparent',
                        'overflow-hidden border-2 rounded-md',
                        '*:max-w-full *:max-h-full *:basis-full',
                    )}
                >
                    {renderFileContent()}
                </div>
            </div>
        </Dialog.Root>
    )
}
